import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";
import type { ComponentProps } from "react";
import { cn } from "@/lib/cn";

const button = cva(
  "inline-flex items-center justify-center gap-2 rounded-xl font-medium whitespace-nowrap transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-[1.15em] [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "bg-amber text-amber-ink hover:brightness-105 active:brightness-95",
        secondary: "bg-surface-2 text-text hover:bg-line",
        ghost: "text-text hover:bg-surface-2",
        danger: "bg-danger text-white hover:brightness-110",
        link: "text-amber-text underline-offset-4 hover:underline px-0 h-auto",
      },
      size: {
        md: "h-12 px-5 text-base",
        sm: "h-9 px-3 text-sm rounded-lg",
        icon: "size-10 rounded-lg",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

type ButtonProps = ComponentProps<"button"> &
  VariantProps<typeof button> & { asChild?: boolean; pending?: boolean };

export function Button({
  className,
  variant,
  size,
  asChild = false,
  pending = false,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const Component = asChild ? Slot : "button";
  return (
    <Component
      className={cn(button({ variant, size }), className)}
      disabled={disabled || pending}
      aria-busy={pending || undefined}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {pending && <LoaderCircle className="animate-spin" aria-hidden />}
          {children}
        </>
      )}
    </Component>
  );
}
